/**
 * Subject Quick Switcher — Horizontal card strip for rapid subject switching.
 *
 * DRR-256: Part of the unified Subject Dashboard.
 * Allows care staff to quickly switch between subjects without losing context.
 * Uses lifecycle phase badge colors (onboarding=blue, active=green, exiting=yellow, closed=gray).
 *
 * DRR-055: Dumb frontend — labels from settingBundle.subjectLabel.
 * DRR-135: Data from backend-filtered mySubjects query.
 */

import { useState } from 'react';
import {
  Group,
  Button,
  Badge,
  Text,
  Popover,
  Stack,
  ScrollArea,
  UnstyledButton,
  Box,
} from '@mantine/core';
import { IconChevronDown, IconUser } from '@tabler/icons-react';

// ============================================================================
// Types
// ============================================================================

export interface Subject {
  readonly id: string;
  readonly displayName: string;
  readonly category?: string | null;
  readonly lifecyclePhase?: string | null;
  readonly active?: boolean;
}

export interface SubjectQuickSwitcherProps {
  readonly subjects: Subject[];
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
  readonly maxVisible?: number;
  readonly subjectLabel?: string;
  readonly loading?: boolean;
}

// ============================================================================
// Phase colors (from wf-0-12-001 lifecycle states)
// ============================================================================

const PHASE_COLOR: Record<string, string> = {
  onboarding: 'blue',
  active: 'green',
  exiting: 'yellow',
  closed: 'gray',
};

function getPhaseColor(phase?: string | null): string {
  if (!phase) return 'gray';
  return PHASE_COLOR[phase.toLowerCase()] ?? 'gray';
}

// ============================================================================
// SubjectQuickSwitcher
// ============================================================================

export function SubjectQuickSwitcher({
  subjects,
  selectedId,
  onSelect,
  maxVisible = 5,
  subjectLabel = 'Subject',
  loading = false,
}: SubjectQuickSwitcherProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);

  if (loading) {
    return (
      <Group gap="xs">
        {[1, 2, 3].map((i) => (
          <Button key={i} variant="light" color="gray" size="xs" loading>
            Loading...
          </Button>
        ))}
      </Group>
    );
  }

  if (subjects.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No {subjectLabel.toLowerCase()}s assigned.
      </Text>
    );
  }

  const visibleSubjects = subjects.slice(0, maxVisible);
  const overflowSubjects = subjects.slice(maxVisible);
  const hasOverflow = overflowSubjects.length > 0;

  const selectedSubject = subjects.find((s) => s.id === selectedId);

  return (
    <Group gap="xs" wrap="nowrap">
      {visibleSubjects.map((subject) => {
        const isSelected = subject.id === selectedId;
        return (
          <Button
            key={subject.id}
            variant={isSelected ? 'filled' : 'light'}
            color={isSelected ? 'blue' : 'gray'}
            size="xs"
            onClick={() => onSelect(subject.id)}
            leftSection={<IconUser size={14} />}
            rightSection={
              subject.lifecyclePhase ? (
                <Badge
                  size="xs"
                  color={getPhaseColor(subject.lifecyclePhase)}
                  variant="dot"
                />
              ) : null
            }
          >
            {subject.displayName}
          </Button>
        );
      })}

      {hasOverflow && (
        <Popover
          opened={popoverOpen}
          onChange={setPopoverOpen}
          position="bottom-end"
          shadow="md"
        >
          <Popover.Target>
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              rightSection={<IconChevronDown size={14} />}
              onClick={() => setPopoverOpen((o) => !o)}
            >
              +{overflowSubjects.length} more
            </Button>
          </Popover.Target>
          <Popover.Dropdown>
            <ScrollArea.Autosize mah={300}>
              <Stack gap="xs">
                {overflowSubjects.map((subject) => (
                  <UnstyledButton
                    key={subject.id}
                    onClick={() => {
                      onSelect(subject.id);
                      setPopoverOpen(false);
                    }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 4,
                      backgroundColor:
                        subject.id === selectedId
                          ? 'var(--mantine-color-blue-light)'
                          : undefined,
                    }}
                  >
                    <Group gap="xs" wrap="nowrap">
                      <IconUser size={14} />
                      <Box>
                        <Text size="sm">{subject.displayName}</Text>
                        {subject.category && (
                          <Text size="xs" c="dimmed">
                            {subject.category}
                          </Text>
                        )}
                      </Box>
                      {subject.lifecyclePhase && (
                        <Badge
                          size="xs"
                          color={getPhaseColor(subject.lifecyclePhase)}
                          ml="auto"
                        >
                          {subject.lifecyclePhase}
                        </Badge>
                      )}
                    </Group>
                  </UnstyledButton>
                ))}
              </Stack>
            </ScrollArea.Autosize>
          </Popover.Dropdown>
        </Popover>
      )}

      {selectedSubject && (
        <Text size="xs" c="dimmed" ml="md">
          Viewing: <strong>{selectedSubject.displayName}</strong>
        </Text>
      )}
    </Group>
  );
}
